import { Plus, Minus, Check, Heart, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useState } from 'react';
import { ImageModal } from './ImageModal';

const PLACEHOLDER = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400";

const formatPrice = (price) => {
    if (!price && price !== 0) return null;
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 0
    }).format(price);
};

// Obtener precio - los precios de lista ya vienen calculados excepto para millar
const obtenerPrecio = (producto, marcaSeleccionada = null) => {
    let precio = producto.precio || 0;
    let unidades = 1;
    
    // Si hay marca seleccionada, usar su precio de lista
    if (marcaSeleccionada) {
        const listaPrecios = marcaSeleccionada.listas_precios || {};
        const primerPrecio = Object.values(listaPrecios)[0];
        if (primerPrecio) precio = primerPrecio;
        if (marcaSeleccionada.unidades_paquete) unidades = marcaSeleccionada.unidades_paquete;
    } else {
        // Si el producto tiene listas_precios directamente
        if (producto.listas_precios) {
            const primerPrecio = Object.values(producto.listas_precios)[0];
            if (primerPrecio) precio = primerPrecio;
        }
        unidades = producto.unidades_paquete || 1;
    }
    
    // Solo para tipo "millar" hay que calcular el precio por caja
    if (producto.tipo_calculo_costo === 'millar' && unidades > 1) {
        return (precio / 1000) * unidades;
    }
    
    // Para el resto, el precio ya viene calculado
    return precio;
};

export const ProductCard = ({ product, onToggleFavorite, isFavorite }) => {
    const { addItem, getItemQuantity, updateQuantity } = useCart();
    const [imageError, setImageError] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    const [localInput, setLocalInput] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    
    const marcas = product.marcas || [];
    const hasBrands = marcas.length > 0;
    const firstBrand = marcas[0] || {};
    const [selectedBrandName, setSelectedBrandName] = useState(firstBrand.nombre || '');
    
    // Get selected brand object
    const selectedBrand = hasBrands ? marcas.find(m => m.nombre === selectedBrandName) || firstBrand : null;

    const quantity = getItemQuantity(product.product_id);
    
    // Get units per package (from brand or product)
    const unidadesPaquete = selectedBrand?.unidades_paquete || product.unidades_paquete;
    
    // Get price directly from lista de precios
    const precioCalculado = obtenerPrecio(product, selectedBrand);
    
    // Check if brand is available
    const marcaDisponible = selectedBrand ? selectedBrand.disponible !== false : true;
    
    // Build array of all images
    
    // Build array of all images
    const getAllImages = () => {
        const images = [];
        
        // First priority: selected brand photo
        if (hasBrands && selectedBrandName) {
            for (let i = 0; i < marcas.length; i++) {
                if (marcas[i].nombre === selectedBrandName && marcas[i].foto) {
                    images.push(marcas[i].foto);
                    break;
                }
            }
        }
        
        // Second: main product photo
        if (product.foto && !images.includes(product.foto)) {
            images.push(product.foto);
        }
        
        // Third: photos from fotos array
        const fotos = product.fotos || [];
        for (let i = 0; i < fotos.length; i++) {
            if (fotos[i] && !images.includes(fotos[i])) {
                images.push(fotos[i]);
            }
        }
        
        // If still no images and has brands, try to get any brand photo
        if (images.length === 0 && hasBrands) {
            for (let i = 0; i < marcas.length; i++) {
                if (marcas[i].foto) {
                    images.push(marcas[i].foto);
                    break;
                }
            }
        }
        
        return images.length > 0 ? images : [PLACEHOLDER];
    };
    
    const allImages = getAllImages();
    const hasMultipleImages = allImages.length > 1;

    const handlePrevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
    };

    const handleNextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
    };

    const handleAdd = () => {
        setIsAdding(true);
        addItem(product);
        setTimeout(() => setIsAdding(false), 300);
    };

    return (
        <div className="product-card animate-fade-in-up">
            <div className="relative group">
                <img
                    src={imageError ? PLACEHOLDER : allImages[currentImageIndex]}
                    alt={product.titulo || product.nombre || 'Producto'}
                    className="product-card-image cursor-pointer"
                    onError={() => setImageError(true)}
                    onClick={() => setShowImageModal(true)}
                    loading="lazy"
                />
                
                {/* Zoom icon overlay */}
                <div 
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center cursor-pointer"
                    onClick={() => setShowImageModal(true)}
                >
                    <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                
                {/* Image navigation arrows */}
                {hasMultipleImages && (
                    <>
                        <button
                            onClick={handlePrevImage}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-opacity z-10"
                            aria-label="Foto anterior"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            onClick={handleNextImage}
                            className="absolute right-12 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-opacity z-10"
                            aria-label="Foto siguiente"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        
                        {/* Image dots indicator - always visible */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/30 rounded-full px-2 py-1 z-10">
                            {allImages.map((img, idx) => (
                                <button
                                    key={`dot-${idx}-${img.slice(-10)}`}
                                    onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                                    className={'w-2 h-2 rounded-full transition-all ' + 
                                        (idx === currentImageIndex ? 'bg-white' : 'bg-white/50')}
                                    aria-label={'Ir a foto ' + (idx + 1)}
                                />
                            ))}
                        </div>
                    </>
                )}
                
                {/* Favorite button */}
                <button
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(product); }}
                    className={'absolute top-2 right-2 p-2 rounded-full transition-all z-10 ' + 
                        (isFavorite ? 'bg-red-500 text-white' : 'bg-white/80 text-stone-400')}
                >
                    <Heart className="h-5 w-5" fill={isFavorite ? 'currentColor' : 'none'} />
                </button>
                
                {/* Quantity badge */}
                {quantity > 0 && (
                    <div className="absolute top-2 left-2 bg-[#007a3f] text-white rounded-full h-7 w-7 flex items-center justify-center text-sm font-bold z-10">
                        {quantity}
                    </div>
                )}
            </div>

            <div className="product-card-content">
                <h3 className="font-semibold text-stone-900 text-base line-clamp-2 mb-1">
                    {product.titulo || product.nombre || 'Sin nombre'}
                </h3>
                
                {/* Medidas */}
                {product.tiene_medidas && product.ancho && product.largo && (
                    <p className="text-sm text-stone-500 mb-1">{product.ancho}m x {product.largo}m = {product.m2} m²</p>
                )}
                
                {/* Unidades por paquete/caja */}
                {unidadesPaquete && (
                    <p className="text-sm text-[#007a3f] font-medium mb-2">
                        📦 {unidadesPaquete} unidades por caja
                    </p>
                )}

                {/* Selector de marcas con precio */}
                {hasBrands && (
                    <div className="mb-3">
                        <select
                            value={selectedBrandName}
                            onChange={(e) => { setSelectedBrandName(e.target.value); setCurrentImageIndex(0); }}
                            className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white"
                        >
                            {marcas.map((m) => {
                                const unidadesMarca = m.unidades_paquete;
                                return (
                                    <option key={m.nombre || `marca-${m.es_principal}`} value={m.nombre || ''} disabled={m.disponible === false}>
                                        {m.nombre || 'Sin marca'}
                                        {m.es_principal ? ' ★' : ''}
                                        {unidadesMarca ? ` (${unidadesMarca} u.)` : ''}
                                        {m.disponible === false ? ' - Sin stock' : ''}
                                    </option>
                                );
                            })}
                        </select>
                        {!marcaDisponible && (
                            <p className="text-xs text-red-500 mt-1">Esta marca no está disponible</p>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-2">
                    <div>
                        {precioCalculado > 0 ? (
                            <span className="price-display">{formatPrice(precioCalculado)}</span>
                        ) : (
                            <span className="text-sm text-stone-400">Consultar</span>
                        )}
                    </div>

                    {quantity === 0 ? (
                        <Button onClick={handleAdd} className={'btn-primary h-10 px-4 ' + (isAdding ? 'bg-green-600' : '')}>
                            {isAdding ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        </Button>
                    ) : (
                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => {
                                    const newQty = quantity - 1;
                                    updateQuantity(product.product_id, newQty);
                                }} 
                                className="quantity-btn"
                            >
                                <Minus className="h-4 w-4" />
                            </button>
                            <input
                                type="number"
                                min="1"
                                value={isEditing ? localInput : quantity}
                                onChange={(e) => {
                                    setLocalInput(e.target.value);
                                    const num = parseInt(e.target.value);
                                    if (!isNaN(num) && num > 0) {
                                        updateQuantity(product.product_id, num);
                                    }
                                }}
                                onFocus={() => {
                                    setIsEditing(true);
                                    setLocalInput(quantity.toString());
                                }}
                                onBlur={() => {
                                    setIsEditing(false);
                                    const num = parseInt(localInput);
                                    if (isNaN(num) || num < 1) {
                                        updateQuantity(product.product_id, 1);
                                    }
                                }}
                                className="w-10 text-center font-semibold text-sm border border-stone-200 rounded py-1"
                            />
                            <button 
                                onClick={() => {
                                    const newQty = quantity + 1;
                                    updateQuantity(product.product_id, newQty);
                                }} 
                                className="quantity-btn"
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Image Modal */}
            <ImageModal 
                images={allImages}
                initialIndex={currentImageIndex}
                isOpen={showImageModal}
                onClose={() => setShowImageModal(false)}
            />
        </div>
    );
};
